import React, { useEffect, useMemo, useRef, useState } from "react";
import "./view-invoice-details.scss";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import { getInvoiceById } from "../../../../../api/order-management";
import { formatDate } from "../../../../../utils/utils";
import Invoice from "../../invoices/create-invoice/invoice-pdf-preview/invoice-pdf";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

interface InvoiceDetailsProps {
  invoiceId?: any;
  previewOnly?: boolean;
}

const InvoiceDetails: React.FC<InvoiceDetailsProps> = ({
  invoiceId,
  previewOnly,
}) => {
  const invoiceRef = useRef(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [invoiceDetails, setInvoiceDetails] = useState<any>(null);

  const fetchInvoiceDetails = async (invoiceId: number) => {
    setLoading(true);
    try {
      const response = await getInvoiceById(invoiceId);
      setInvoiceDetails(response?.data?.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceDetails(invoiceId);
    }
  }, [invoiceId]);

  const handleDownloadPdf = async () => {
    const element = invoiceRef.current;
    if (!element) return;

    const canvas = await html2canvas(element, { scale: 2 });
    const data = canvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "px",
      format: "a4",
    });

    const imgProps = pdf.getImageProperties(data);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(data, "PNG", 0, 0, pdfWidth, pdfHeight);
    const invoiceId = invoiceDetails?.invoice_number || "invoice";
    const timestamp = new Date().toISOString().split("T")[0];
    const fileName = invoiceId
      ? `${invoiceId}-${timestamp}.pdf`
      : `invoice-${timestamp}.pdf`;

    pdf.save(fileName);
  };

  function formatCurrencyWithLabel(
    value: string | number | null | undefined
  ): string {
    if (!value) return "-";

    const amount =
      typeof value === "string" ? value.trim() : value.toString().trim();
    const match = amount.match(/^(\d+(?:\.\d+)?)(?:\s*(\w+))?$/i);

    if (!match) return value.toString(); // fallback in case format doesn't match

    const numericValue = parseFloat(match[1]);
    const label = match[2]
      ? match[2].charAt(0).toUpperCase() + match[2].slice(1).toLowerCase()
      : null;

    const formattedCurrency = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(numericValue);

    return label ? `${formattedCurrency} (${label})` : formattedCurrency;
  }

  const orderData = useMemo(() => {
    if (!invoiceDetails) return null;
    return {
      issueDate: invoiceDetails?.invoice_date,
      companydetails: {
        companyName: invoiceDetails?.company_name,
        email: invoiceDetails?.customer_email,
      },
      companyAddress: invoiceDetails?.address,
    };
  }, [invoiceDetails]);

  const invoiceData = useMemo(() => {
    if (!invoiceDetails) return null;
    return {
      invoiceId: invoiceDetails?.invoice_number,
      issueDate: invoiceDetails?.invoice_date,
      totalAmount: invoiceDetails?.amount,
    };
  }, [invoiceDetails]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <div className="invoice-detail-wrapper">
        {!previewOnly && (
          <div className="invoice-details">
            <div className="invoice-detail-header">
              <h2>Details</h2>
            </div>
            <div className="invoice-detail-content">
              <div className="invoice-detail-info">
                <div className="invoice-detail-item">
                  <p className="label">Customer</p>
                  <p className="value">{invoiceDetails?.customer_name}</p>
                </div>
                <div className="invoice-detail-item">
                  <p className="label">Invoice Status</p>
                  <p className="value">{invoiceDetails?.status}</p>
                </div>
                <div className="invoice-detail-item">
                  <p className="label">Created On</p>
                  <p className="value">
                    {invoiceDetails?.invoice_date
                      ? formatDate(invoiceDetails?.invoice_date)
                      : "-"}
                  </p>
                </div>
                <div className="invoice-detail-item">
                  <p className="label">Invoice ID</p>
                  <p className="value">{invoiceDetails?.invoice_number}</p>
                </div>
                <div className="invoice-detail-item">
                  <p className="label">Amount</p>
                  <p className="value">
                    {formatCurrencyWithLabel(invoiceDetails?.amount)}
                  </p>
                </div>
                <div className="invoice-detail-item">
                  <p className="label">Total Adjustment Amount</p>
                  <p className="value">
                    {formatCurrencyWithLabel(invoiceDetails?.adjustment_amount)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="invoice-preview">
          <div className="invoice-preview-content">
            {invoiceData && orderData && (
              <Invoice
                ref={invoiceRef}
                data={invoiceData}
                orderData={orderData}
                amount={invoiceDetails?.amount}
                issuedDate={invoiceDetails?.invoice_date}
              />
            )}
          </div>
          <div className="download-button-wrapper">
            <button
              type="button"
              className="download-button"
              onClick={handleDownloadPdf}
            >
              Download
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceDetails;
